import { useState } from 'react';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { Switch, Typography } from '@mui/material';

import { TaskBodyStyled } from './taskBody.style';
import { giveNumber } from '../../../../utils/utils';
import { applyDraftStatus } from '../../planner.utils';
import TextInputField from '../../../../components/form-fields/text-input-field/text-input-field';

const TaskBody = ({
  deliverableId,
  task,
  resources, // aka: projectObj.resources or projectDetails.resources
  isGeneralUser,
  handleDeliverableUpdate,
  handleStatusChange,
  project,
}) => {
  const [rawInput, setRawInput] = useState({});

  // Step 1: Ensure all project resources are reflected in task.resources
  const mergedResources = useMemo(() => {
    return resources.map((projRes) => {
      const existing = task.resources?.find((r) => r.id === projRes.id);
      return (
        existing || {
          id: projRes.id,
          isEnabled: false,
          hours: 0,
          organization: projRes.organization || 'FKA',
        }
      );
    });
  }, [resources, task.resources]);

  const handleHoursChange = (value, resource, projRes) => {
    const hours = giveNumber(value);
    const shouldEnable = hours > 0;
    const actionType = shouldEnable && !resource.isEnabled ? 'enable' : undefined;

    const updated = {
      ...resource,
      hours,
      isEnabled: shouldEnable,
    };
    handleDeliverableUpdate(deliverableId, task.id, resource.id, updated, actionType);

    const calculatedValue = hours * giveNumber(projRes?.rate);
    setRawInput((prev) => ({
      ...prev,
      [resource.id]: value,
      [`value_${resource.id}`]: calculatedValue,
    }));
  };

  const handleValueChange = (value, resource, projRes) => {
    const total = giveNumber(value);
    const rate = giveNumber(projRes?.rate);
    const hours = rate !== 0 ? total / rate : 0;
    const shouldEnable = hours > 0;
    const actionType = shouldEnable && !resource.isEnabled ? 'enable' : undefined;

    const updated = {
      ...resource,
      hours,
      isEnabled: shouldEnable,
    };
    handleDeliverableUpdate(deliverableId, task.id, resource.id, updated, actionType);

    setRawInput((prev) => ({
      ...prev,
      [`value_${resource.id}`]: value,
      [resource.id]: hours,
    }));
  };

  const toggleResource = (resource, projRes) => {
    if (!resource?.isEnabled) {
      return;
    }
    const isEnabled = !resource?.isEnabled;
    const updated = {
      ...resource,
      isEnabled,
      hours: 0,
    };

    setRawInput((prev) => {
      const next = { ...prev };
      delete next[resource.id];
      delete next[`value_${resource.id}`];
      return next;
    });

    handleDeliverableUpdate(
      deliverableId,
      task.id,
      resource.id,
      updated,
      isEnabled ? 'enable' : 'disable',
    );
  };

  return (
    <TaskBodyStyled>
      <div>
        {mergedResources.map((resource, i) => {
          const projRes = resources.find((r) => r.id === resource.id);
          if (!projRes?.role) return null;

          const hoursVal =
            rawInput[resource.id] != null ? rawInput[resource.id] : (resource.hours ?? '');

          const valueVal =
            rawInput[`value_${resource.id}`] != null
              ? rawInput[`value_${resource.id}`]
              : giveNumber(resource.hours) * giveNumber(projRes?.rate);

          return (
            <div
              key={resource.id}
              className={classNames('task-row', {
                'row-border': i !== mergedResources.length - 1,
              })}
            >
              <div className="role switch">
                <Switch
                  checked={!!resource.isEnabled}
                  disabled={isGeneralUser}
                  onClick={() => toggleResource(resource, projRes)}
                  className={!resource.isEnabled ? 'not-enabled-switch' : ''}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: resource?.isEnabled ? '#301D35' : 'rgba(0, 0, 0, 0.38)',
                  }}
                >
                  {projRes.role}
                </Typography>
              </div>
              <div className="actions">
                <div className="text-input">
                  <TextInputField
                    value={hoursVal}
                    inputType="text"
                    disabled={isGeneralUser}
                    onChange={(e) => handleHoursChange(e.target.value, resource, projRes)}
                    onFocus={() =>
                      setRawInput((prev) => ({
                        ...prev,
                        [resource.id]: resource?.hours || '',
                      }))
                    }
                    onBlur={() =>
                      setRawInput((prev) => ({
                        ...prev,
                        [resource.id]: null,
                      }))
                    }
                  />
                </div>
                <div className="text-input">
                  <TextInputField
                    value={valueVal}
                    inputType="text"
                    disabled={isGeneralUser}
                    onChange={(e) => {
                      applyDraftStatus(project, handleStatusChange);
                      handleValueChange(e.target.value, resource, projRes);
                    }}
                    onFocus={() =>
                      setRawInput((prev) => ({
                        ...prev,
                        [`value_${resource.id}`]: valueVal,
                      }))
                    }
                    onBlur={() =>
                      setRawInput((prev) => ({
                        ...prev,
                        [`value_${resource.id}`]: null,
                      }))
                    }
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </TaskBodyStyled>
  );
};

export default TaskBody;
